import { Controller } from "@hotwired/stimulus"

// Puts its items in the order an admin drags them into (or moves them with
// the up/down buttons inside each item). Every change renumbers the
// `data-sortable-position` inputs and `data-sortable-number` labels inside
// the items; with a `url`, the new order is also PATCHed straight away as
// `{ ids: [...] }` read from each item's `data-id`.
//
// Only the element marked draggable — a handle — starts a drag, so text in
// inputs inside an item still selects normally.
export default class extends Controller {
  static targets = ["item", "status"]
  static values = {
    url: String,
    param: { type: String, default: "ids" }
  }

  connect() {
    this.renumber()
  }

  dragStart(event) {
    const item = this.itemFrom(event.target)
    if (!item) return

    this.dragged = item
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", item.dataset.id || "")
    item.classList.add("is-dragging")
  }

  dragOver(event) {
    if (!this.dragged) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const over = this.itemFrom(event.target)
    if (!over || over === this.dragged) return

    const box = over.getBoundingClientRect()
    const below = event.clientY > box.top + box.height / 2
    over.parentNode.insertBefore(this.dragged, below ? over.nextSibling : over)
  }

  drop(event) {
    event.preventDefault()
  }

  dragEnd() {
    if (!this.dragged) return

    this.dragged.classList.remove("is-dragging")
    this.dragged = null
    this.changed()
  }

  moveUp(event) {
    const item = this.itemFrom(event.currentTarget)
    const previous = this.neighbour(item, -1)
    if (!previous) return

    item.parentNode.insertBefore(item, previous)
    this.changed()
    event.currentTarget.focus()
  }

  moveDown(event) {
    const item = this.itemFrom(event.currentTarget)
    const next = this.neighbour(item, +1)
    if (!next) return

    item.parentNode.insertBefore(next, item)
    this.changed()
    event.currentTarget.focus()
  }

  changed() {
    this.renumber()
    if (this.hasUrlValue) this.save()
  }

  renumber() {
    this.visibleItems.forEach((item, index) => {
      item.querySelectorAll("[data-sortable-position]").forEach((input) => (input.value = index + 1))
      item.querySelectorAll("[data-sortable-number]").forEach((label) => (label.textContent = index + 1))
    })
  }

  async save() {
    const ids = this.visibleItems.map((item) => item.dataset.id)
    this.announce("Saving order…")

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ [this.paramValue]: ids })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.announce("Order saved")
    } catch (error) {
      this.announce("Could not save the order — reload and try again")
    }
  }

  announce(text) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = text
    clearTimeout(this.statusTimer)
    this.statusTimer = setTimeout(() => (this.statusTarget.textContent = ""), 2500)
  }

  // Items removed from a form are hidden rather than deleted (so Rails still
  // receives their _destroy flag); they no longer take part in the order.
  get visibleItems() {
    return this.itemTargets.filter((item) => !item.hidden)
  }

  neighbour(item, direction) {
    const items = this.visibleItems
    return items[items.indexOf(item) + direction]
  }

  itemFrom(element) {
    return element?.closest?.("[data-sortable-target='item']")
  }
}
